import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    VStack,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Text,
    Flex,
    useToast
} from '@chakra-ui/react';
import { useState, useContext, useEffect, useMemo } from 'react';
import PartBayButton from '../components/PartBayButton';
import { useInviteUser } from '../hooks/useInviteUser';
import { Role } from '../hooks/useValidateSession';
import SessionContext, { isInventoryOrgSession, isInvoicingOrgSession } from '../contexts/SessionContext';
import LocationSelector from '../components/LocationSelector';
import { getInvitePositionOptions } from '../utils/invitePositionOptions';
import SearchableSingleSelect from '../components/SearchableSingleSelect';
import { checkboxMenuTriggerMetrics } from '../ui';
import { useLockModalScroll } from '../hooks/useLockModalScroll';
import { useGetSettings } from '../hooks/useGetSettings';
import { isValidInviteEmail } from '../utils/isValidInviteEmail';

interface InviteUserModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const InviteUserModal = ({ isOpen, onClose }: InviteUserModalProps) => {
    const toast = useToast();
    const inviteUserMutation = useInviteUser();
    const sessionContext = useContext(SessionContext);
    const isInvoicingOrg = isInvoicingOrgSession(sessionContext);
    const isInventoryOrg = isInventoryOrgSession(sessionContext);
    const { data: settingsData } = useGetSettings();
    const orgInventoryEnabled = !!settingsData?.inventoryEnabled;
    const orgInvoicingEnabled = !!settingsData?.invoicingEnabled;

    const positionOptions = useMemo(() => {
        if (isInvoicingOrg) {
            return [{ value: 'invoicing' as Role, label: 'Invoicing' }];
        }
        return getInvitePositionOptions({
            includeTechnician: orgInventoryEnabled,
            includeInvoicing: orgInvoicingEnabled,
            inventoryOrg: isInventoryOrg,
        });
    }, [isInvoicingOrg, isInventoryOrg, orgInventoryEnabled, orgInvoicingEnabled]);

    const [email, setEmail] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    // No default position — an empty field is the call to pick one, and Send
    // stays disabled until it is answered.
    const [position, setPosition] = useState<Role | ''>('');
    const [locationId, setLocationId] = useState<Array<string> | 'all'>([]);

    const hasLocationSelection = locationId === 'all' || locationId.length > 0;
    const emailIsValid = isValidInviteEmail(email);
    const showEmailError = emailTouched && email.trim() !== '' && !emailIsValid;

    useLockModalScroll(isOpen);

    useEffect(() => {
        if (!isOpen) return;
        if (isInvoicingOrg) {
            setPosition('invoicing');
        }
    }, [isOpen, isInvoicingOrg]);

    const handleInviteUser = () => {
        const effectivePosition: Role | '' = isInvoicingOrg ? 'invoicing' : position;
        const trimmedEmail = email.trim();
        if (!isValidInviteEmail(trimmedEmail) || !effectivePosition || !hasLocationSelection) return;

        inviteUserMutation.mutate(
            {
                email: trimmedEmail,
                position: effectivePosition,
                locationId
            },
            {
                onSuccess: () => {
                    toast({
                        title: 'Invitation sent',
                        description: 'The user has been invited to join your organization.',
                        status: 'success',
                        duration: 5000,
                        isClosable: true,
                    });
                    onClose();
                    setEmail('');
                    setEmailTouched(false);
                    // Back to the unanswered state — the next invite should ask again.
                    setPosition(isInvoicingOrg ? 'invoicing' : '');
                    setLocationId([]);
                },
                onError: (error: any) => {
                    toast({
                        title: 'Error sending invitation',
                        description: error.message || 'Failed to send invitation. Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                }
            }
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered trapFocus={false} blockScrollOnMount>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader
                    bg="gray.50"
                    borderTopRadius="md"
                    borderBottomWidth="1px"
                    borderColor="gray.300"
                    fontSize="md"
                    fontWeight="semibold"
                    color="gray.800"
                    py={4}
                >
                    Invite User
                </ModalHeader>
                <ModalCloseButton top={3} />
                <ModalBody>
                    <VStack spacing={4}>
                        <FormControl isRequired isInvalid={showEmailError}>
                            <FormLabel>Email Address</FormLabel>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    setEmail(next);
                                    if (next.trim() === '') setEmailTouched(false);
                                }}
                                onBlur={() => setEmailTouched(true)}
                                placeholder="Enter email address"
                                fontSize={checkboxMenuTriggerMetrics.fontSize}
                                fontWeight={checkboxMenuTriggerMetrics.fontWeight}
                            />
                            {showEmailError && (
                                <FormErrorMessage>Please enter a valid email address</FormErrorMessage>
                            )}
                        </FormControl>

                        <FormControl isRequired isDisabled={isInvoicingOrg}>
                            <FormLabel>Position</FormLabel>
                            <SearchableSingleSelect
                                width="100%"
                                options={positionOptions}
                                value={isInvoicingOrg ? 'invoicing' : position}
                                placeholder="Select position"
                                isDisabled={isInvoicingOrg}
                                onChange={(nextValue) => setPosition(nextValue as Role)}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Location</FormLabel>
                            <LocationSelector
                                selectedLocations={locationId}
                                onLocationChange={setLocationId}
                                width="100%"
                            />
                        </FormControl>

                        <Text
                            fontSize="xs"
                            color="fg.muted"
                            whiteSpace="nowrap"
                            lineHeight="1.3"
                            alignSelf="flex-start"
                        >
                            An invitation email will be sent to this address with signup instructions.
                        </Text>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Flex justify="flex-end" gap={2}>
                        <Button
                            variant="outlineGray"
                            onClick={onClose}
                            size="action"
                        >
                            Cancel
                        </Button>
                        <PartBayButton
                            size="action"
                            variant="solid"
                            colorScheme="partbay-blue"
                            onClick={handleInviteUser}
                            isLoading={inviteUserMutation.isPending}
                            loadingText="Sending..."
                            isDisabled={!emailIsValid || !hasLocationSelection || (!isInvoicingOrg && !position)}
                            text="Send Invitation"
                        />
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default InviteUserModal;
